"use client";

import { SafeChat, SimplifiedUser } from "@/app/types";
import Link from "next/link";
import Container from "../Container";
import Logo from "./Logo";
import Search from "./Search";
import UserMenu from "./UserMenu";
import VerticalNav from "./VerticalNav";
import { ExchangeCurrency } from "@prisma/client";
import CurrencyMenu from "./Currency";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import useRentModal from "@/app/hooks/useRentModal";
import { usePathname, useRouter } from "next/navigation";
import { BiSearch } from "react-icons/bi";
import { IoClose } from "react-icons/io5";

/** Below Tailwind's `sm`: where the search bar collapses behind the magnifier. */
const MOBILE_QUERY = "(max-width: 639px)";
/** Scroll distance before the search folds away on its own. */
const AUTO_COLLAPSE_AFTER_PX = 48;
/** At or below this scroll position the page is "at the top" and the search opens again. */
const TOP_PX = 1;

/**
 * Visitors arriving from Google Things to Do came for one specific activity:
 * on phones the search starts folded away so the listing gets the screen.
 * The magnifier still opens it.
 */
const arrivedFromThingsToDo = () =>
  new URLSearchParams(window.location.search).get("utm_medium") ===
  "things_to_do";

interface NavbarProps {
  currentUser?: SimplifiedUser | null;
  rates?: ExchangeCurrency | null;
  chats: {
    chats: SafeChat[] | null;
    totalPages: number;
    currentPage: number;
  };
}

const Navbar: React.FC<NavbarProps> = ({ currentUser, chats, rates }) => {
  const router = useRouter();
  const pathname = usePathname();
  const navbarRef = useRef<HTMLDivElement>(null);
  const mobileSearchRef = useRef<HTMLDivElement>(null);
  const rentModal = useRentModal();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isCurrencyMenuOpen, setIsCurrencyMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  /** Collapsible search only below sm; from sm up the bar is always visible. Phones start expanded. */
  const [mobileSearchExpanded, setMobileSearchExpanded] = useState(true);
  /**
   * Phones: open at the top of the page, folded away once the user scrolls
   * down, open again back at the top. Between those, the magnifier and the X
   * work as usual. `leftTop` makes the scroll collapse happen once per trip
   * away from the top, so a search reopened mid-page stays open.
   */
  const leftTopRef = useRef(false);
  const contentOffsetSetRef = useRef(false);
  /**
   * Google Things to Do arrivals and the checkout page: the search does not
   * open by itself, so the listing or the booking form gets the screen.
   */
  const keepFoldedRef = useRef(false);
  const forceOffsetSyncRef = useRef(false);

  useLayoutEffect(() => {
    if (!window.matchMedia(MOBILE_QUERY).matches) return;
    const fold =
      arrivedFromThingsToDo() ||
      ["/booking/", "/trips/details/"].some((prefix) =>
        pathname?.startsWith(prefix),
      );
    keepFoldedRef.current = fold;
    if (fold) {
      // The page padding must follow the folded navbar even when the browser
      // restored a scroll position, or a tall gap is left above the content.
      forceOffsetSyncRef.current = true;
      setMobileSearchExpanded(false);
    }
  }, [pathname]);

  const toggleUserMenu = useCallback(() => {
    setIsUserMenuOpen((prev) => !prev);
    setIsCurrencyMenuOpen(false);
  }, []);

  const toggleCurrencyMenu = useCallback(() => {
    setIsCurrencyMenuOpen((prev) => !prev);
    setIsUserMenuOpen(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        navbarRef.current &&
        !navbarRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false);
        setIsCurrencyMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const mobile = window.matchMedia(MOBILE_QUERY);
    const handleScroll = () => {
      if (!mobile.matches) return;
      const y = window.scrollY;
      if (y <= TOP_PX) {
        if (leftTopRef.current) {
          leftTopRef.current = false;
          if (!keepFoldedRef.current) setMobileSearchExpanded(true);
        }
      } else if (y > AUTO_COLLAPSE_AFTER_PX && !leftTopRef.current) {
        leftTopRef.current = true;
        // Never pull the field away from someone typing in it (the on-screen
        // keyboard can scroll the page).
        const typing = mobileSearchRef.current?.contains(
          document.activeElement,
        );
        if (!typing) setMobileSearchExpanded(false);
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /** FABs + mobile padding use this; updates when search/categories toggle. */
  useLayoutEffect(() => {
    const el = navbarRef.current;
    if (!el) return;
    const sync = () => {
      const bottom = Math.round(el.getBoundingClientRect().bottom);
      document.documentElement.style.setProperty(
        "--nav-fab-offset",
        `${bottom + 4}px`,
      );
      // On phones the page padding only follows the navbar at the top of the
      // page. Scrolled down, opening or folding the search overlays the page
      // instead of shifting it (which would also feed back into scrollY).
      const forced = forceOffsetSyncRef.current && !mobileSearchExpanded;
      if (
        forced ||
        !contentOffsetSetRef.current ||
        window.scrollY <= TOP_PX ||
        !window.matchMedia(MOBILE_QUERY).matches
      ) {
        if (forced) forceOffsetSyncRef.current = false;
        contentOffsetSetRef.current = true;
        document.documentElement.style.setProperty(
          "--nav-content-offset",
          `${bottom + 4}px`,
        );
      }
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    window.addEventListener("resize", sync);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", sync);
    };
  }, [mobileSearchExpanded]);

  useEffect(() => {
    return () => {
      document.documentElement.style.removeProperty("--nav-fab-offset");
      document.documentElement.style.removeProperty("--nav-content-offset");
    };
  }, []);

  return (
    <>
      <div
        className="shrink-0 transition-[height] duration-200 ease-out"
        style={{ height: "var(--promo-banner-offset, 0px)" }}
        aria-hidden
      />
      <div
        ref={navbarRef}
        className={`
          fixed 
          inset-x-0
          z-30 
          transition-all 
          duration-200
          ${
            isScrolled
              ? "bg-white/95 backdrop-blur border-b border-gray-200 shadow-sm"
              : "bg-white/70 backdrop-blur-lg"
          }
        `}
        style={{ top: "var(--promo-banner-offset, 0px)" }}
      >
        <Container>
          <div className="flex flex-col gap-2 sm:gap-3 pt-2 pb-1 sm:pt-3 sm:pb-2">
            <div className="flex flex-row items-center justify-between gap-3 sm:gap-6">
              <div className="flex-shrink-0">
                <Logo />
              </div>

              <div className="hidden flex-1 sm:flex sm:justify-center">
                <div className="max-w-xl w-full">
                  <Search currentUser={currentUser} rates={rates} />
                </div>
              </div>

              <div className="flex flex-row items-center gap-1.5 sm:gap-3">
                {currentUser?.userType === "Center" && (
                  <button
                    onClick={rentModal.onOpen}
                    className="hidden lg:inline-flex items-center text-sm font-semibold py-2.5 px-4 rounded-full border border-gray-200 hover:border-cyan-700 hover:text-cyan-800 hover:shadow-sm transition bg-white"
                  >
                    List your courses
                  </button>
                )}
                {!currentUser && (
                  <button
                    onClick={() => router.push("/registercenter")}
                    className="hidden lg:inline-flex items-center text-sm font-semibold py-2.5 px-4 rounded-full border border-gray-200 hover:border-cyan-700 hover:text-cyan-800 hover:shadow-sm transition bg-white text-center"
                  >
                    Register as a DIVE CENTER
                  </button>
                )}
                <button
                  type="button"
                  aria-label={
                    mobileSearchExpanded
                      ? "Hide search and categories"
                      : "Show search and categories"
                  }
                  aria-expanded={mobileSearchExpanded}
                  className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-cyan-700 text-white shadow-sm transition hover:bg-cyan-600 sm:hidden"
                  onClick={() => setMobileSearchExpanded((open) => !open)}
                >
                  {mobileSearchExpanded ? (
                    <IoClose size={20} />
                  ) : (
                    <BiSearch size={20} />
                  )}
                </button>
                <Link
                  href="/gifts"
                  className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-1 py-2 text-xs font-semibold text-gray-700 hover:text-cyan-800 sm:px-2 sm:text-sm"
                >
                  Gift cards{" "}
                  <span className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[9px] font-bold tracking-wide text-sky-800 sm:text-[10px]">
                    NEW
                  </span>
                </Link>
                <CurrencyMenu
                  currentUser={currentUser}
                  isOpen={isCurrencyMenuOpen}
                  toggleOpen={toggleCurrencyMenu}
                />
                <UserMenu
                  isOpen={isUserMenuOpen}
                  toggleOpen={toggleUserMenu}
                  chatsData={chats}
                />
              </div>
            </div>

            {mobileSearchExpanded ? (
              <div
                ref={mobileSearchRef}
                className="border-t border-cyan-100/60 bg-white/90 pt-2 pb-1 sm:hidden"
              >
                <Search
                  currentUser={currentUser}
                  rates={rates}
                  onSearchComplete={() => setMobileSearchExpanded(false)}
                />
              </div>
            ) : null}
          </div>
        </Container>

        <div
          className={
            mobileSearchExpanded
              ? "border-t border-gray-100"
              : "border-t border-gray-100 max-sm:hidden"
          }
        >
          <VerticalNav />
        </div>
      </div>
    </>
  );
};

export default Navbar;
